import { useListTagsQuery } from '@vizit/api';
import { Button, ButtonLevel, Menu, Popover } from '@vizit/atoms';
import { CheckIcon, XCloseIcon } from '@vizit/icons';
import { SelectTrigger } from '@vizit/molecules';
import { cx } from '@vizit/theme';
import * as React from 'react';
import type { Selection } from 'react-aria-components';
import { Autocomplete, MenuItem } from 'react-aria-components';
import { TAG_SEARCH_LIMIT, TagSearchField, useTagSearch } from '../tag-search/tag-search';

const TAG_IDS_SEPARATOR = ',';

function parseTagIdsParam(value: unknown): string[] | undefined {
  if (typeof value !== 'string') return undefined;
  const tagIds = [...new Set(value.split(TAG_IDS_SEPARATOR).map((id) => id.trim()))].filter(Boolean);
  return tagIds.length ? tagIds : undefined;
}

function formatTagIdsParam(tagIds: string[] | undefined): string | undefined {
  return tagIds?.length ? tagIds.join(TAG_IDS_SEPARATOR) : undefined;
}

interface TagFilterProps {
  workspaceId: string;
  selectedTagIds: string[];
  onSelectedTagIdsChange: (tagIds: string[]) => void;
  className?: string;
  'data-testid'?: string;
}

function TagFilter(props: TagFilterProps) {
  const { workspaceId, selectedTagIds, onSelectedTagIdsChange, className, 'data-testid': testId } = props;
  const [search, setSearch] = React.useState('');
  const { name, tags, isSettled } = useTagSearch(workspaceId, search);
  const { data: allTags } = useListTagsQuery({ workspace_id: workspaceId, search: '', limit: TAG_SEARCH_LIMIT });
  const tagNames = React.useRef(new Map<string, string>());
  for (const tag of [...(allTags?.data ?? []), ...tags]) {
    tagNames.current.set(tag.id, tag.name);
  }

  const label =
    selectedTagIds.length === 0
      ? 'All tags'
      : selectedTagIds.length === 1
        ? (tagNames.current.get(selectedTagIds[0]) ?? '1 tag')
        : `${selectedTagIds.length} tags`;

  const handleSelectionChange = (keys: Selection) => {
    if (keys === 'all') return;
    const shown = new Set(tags.map((tag) => tag.id));
    const kept = selectedTagIds.filter((id) => !shown.has(id));
    onSelectedTagIdsChange([...kept, ...[...keys].map(String).filter((id) => shown.has(id))]);
  };

  return (
    <div className={cx('relative', className)} data-testid={testId}>
      <Popover.Trigger onOpenChange={(isOpen) => !isOpen && setSearch('')}>
        <SelectTrigger aria-label={`Filter by tags, ${label}`}>
          <span className={cx(selectedTagIds.length > 0 && 'pr-12')}>{label}</span>
        </SelectTrigger>
        <Popover placement="bottom start" offset={4} className="w-[--trigger-width] min-w-[280px] shadow-md">
          <Popover.Dialog aria-label="Filter by tags">
            <Autocomplete inputValue={search} onInputChange={setSearch}>
              <TagSearchField label="Search tags" />
              <Menu
                aria-label="Tags"
                items={tags}
                selectionMode="multiple"
                escapeKeyBehavior="none"
                selectedKeys={selectedTagIds}
                onSelectionChange={handleSelectionChange}
                className="max-h-[228px] overflow-y-auto py-2"
                renderEmptyState={() => (
                  <p className="px-8 py-5 text-sm text-tertiary">
                    {!isSettled ? 'Searching…' : name ? 'No tags found' : 'No tags yet'}
                  </p>
                )}
              >
                {(tag) => (
                  <MenuItem
                    id={tag.id}
                    textValue={tag.name}
                    aria-label={`${tag.name}, ${tag.image_count} ${tag.image_count === 1 ? 'image' : 'images'}`}
                    className={cx(
                      'group mx-3 flex h-19 cursor-default select-none items-center gap-6 px-5 outline-none',
                      'text-md text-secondary data-[focused]:bg-primary-hover',
                    )}
                  >
                    <span
                      aria-hidden
                      className={cx(
                        'flex h-8 w-8 shrink-0 items-center justify-center rounded-xxs border border-primary',
                        'group-data-[selected]:border-brand-600 group-data-[selected]:bg-brand-600 group-data-[selected]:text-white',
                      )}
                    >
                      <CheckIcon size={10} className="hidden group-data-[selected]:block" />
                    </span>
                    <span className="flex-1 truncate font-medium">{tag.name}</span>
                    <span className="text-sm text-tertiary">{tag.image_count}</span>
                  </MenuItem>
                )}
              </Menu>
            </Autocomplete>
          </Popover.Dialog>
        </Popover>
      </Popover.Trigger>
      {selectedTagIds.length > 0 && (
        <Button
          aria-label="Clear tag filter"
          level={ButtonLevel['Text-Secondary']}
          className="text-gray-utility-400 hover:text-gray-utility-600 absolute right-14 top-1/2 -translate-y-1/2 z-10"
          onPress={() => onSelectedTagIdsChange([])}
        >
          <XCloseIcon size={16} />
        </Button>
      )}
    </div>
  );
}

export { TagFilter, formatTagIdsParam, parseTagIdsParam };
export type { TagFilterProps };
